/**
 * Cherry Blossom night/day backdrop: a soft sun, snow-capped Mt. Fuji with
 * layered hills along the bottom, and a detailed cherry branch (5-petal
 * blossoms) framing the top — behind content, `pointer-events: none`. Scene
 * colors come from theme vars so it recolors for light (spring day) / dark
 * (dusk). The sky gradient itself is on the theme `body` (CSS).
 */
import React from 'react';

import { Z } from '@shared/constants/zIndex';

/** Fixed Mt. Fuji + cherry-branch backdrop. */
export const CherryBlossomBackdrop: React.FC = () => (
  <div
    aria-hidden
    data-testid="cherry-blossom-backdrop"
    className="pointer-events-none fixed inset-0"
    style={{ zIndex: Z.BACKDROP }}
  >
    {/* Soft sun. */}
    <div
      className="absolute h-20 w-20 rounded-full"
      style={{
        top: '8%',
        left: '50%',
        transform: 'translateX(-50%)',
        background: 'radial-gradient(circle, oklch(0.98 0.03 75), oklch(0.95 0.045 350))',
        opacity: 0.5,
      }}
    />

    {/* Mt. Fuji + hills (bottom, behind the branch). */}
    <svg
      className="absolute bottom-0 left-0 w-full"
      style={{ height: '60%' }}
      viewBox="0 0 400 150"
      preserveAspectRatio="xMidYMax slice"
    >
      <path d="M86,150 Q150,66 200,30 Q250,66 314,150 Z" fill="var(--fuji)" />
      <path d="M200,30 Q250,66 314,150 L250,150 Q224,80 200,46 Z" fill="var(--fuji)" opacity="0.7" />
      <path
        d="M171,64 Q186,42 200,32 Q214,42 229,64 Q221,58 214,67 Q207,55 200,66 Q193,55 186,67 Q179,58 171,64 Z"
        fill="var(--fuji-snow)"
      />
      <path
        d="M196,46 Q198,70 192,92 M205,48 Q207,74 214,96"
        stroke="var(--fuji-snow)"
        strokeWidth="2.4"
        fill="none"
      />
      <path d="M0,150 Q120,126 240,132 T400,128 V150 Z" fill="var(--hill)" />
      <path d="M0,150 Q150,138 300,142 T400,140 V150 Z" fill="var(--hill)" opacity="0.85" />
    </svg>

    {/* Cherry branch with blossoms (top). */}
    <svg
      className="absolute top-0 left-0 w-full"
      style={{ height: '42%' }}
      viewBox="0 0 400 110"
      preserveAspectRatio="xMidYMin slice"
    >
      <defs>
        <symbol id="cb-bloom" viewBox="-10 -10 20 20">
          <g fill="var(--blossom)">
            <ellipse cx="0" cy="-6" rx="3" ry="4.6" />
            <ellipse cx="0" cy="-6" rx="3" ry="4.6" transform="rotate(72)" />
            <ellipse cx="0" cy="-6" rx="3" ry="4.6" transform="rotate(144)" />
            <ellipse cx="0" cy="-6" rx="3" ry="4.6" transform="rotate(216)" />
            <ellipse cx="0" cy="-6" rx="3" ry="4.6" transform="rotate(288)" />
          </g>
          <circle r="1.6" fill="oklch(0.86 0.13 88)" />
        </symbol>
      </defs>
      <g stroke="var(--branch)" fill="none" strokeLinecap="round">
        <path d="M-5,12 Q110,34 240,20 T405,30" strokeWidth="5" />
        <path d="M90,28 Q102,50 104,72 M240,20 Q246,42 236,60 M320,24 Q327,44 338,60" strokeWidth="2.4" />
      </g>
      <use href="#cb-bloom" x="6" y="-2" width="24" height="24" />
      <use href="#cb-bloom" x="44" y="8" width="18" height="18" />
      <use href="#cb-bloom" x="78" y="13" width="22" height="22" />
      <use href="#cb-bloom" x="98" y="62" width="20" height="20" />
      <use href="#cb-bloom" x="124" y="8" width="24" height="24" />
      <use href="#cb-bloom" x="166" y="6" width="20" height="20" />
      <use href="#cb-bloom" x="196" y="10" width="24" height="24" />
      <use href="#cb-bloom" x="228" y="48" width="18" height="18" />
      <use href="#cb-bloom" x="238" y="7" width="22" height="22" />
      <use href="#cb-bloom" x="276" y="11" width="18" height="18" />
      <use href="#cb-bloom" x="312" y="10" width="24" height="24" />
      <use href="#cb-bloom" x="330" y="52" width="18" height="18" />
      <use href="#cb-bloom" x="352" y="16" width="20" height="20" />
      <use href="#cb-bloom" x="382" y="22" width="22" height="22" />
    </svg>
  </div>
);
